import { useRef, useState, type KeyboardEvent } from 'react';
import {
  addDays,
  addMonths,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const WEEK_OPTIONS = { locale: es, weekStartsOn: 1 } as const;
const KEY = 'yyyy-MM-dd';

export interface MonthGridProps {
  month: Date;
  selected: Date | null;
  onSelect: (date: Date) => void;
  /** Arrow keys walk out of the month; the popover follows them. */
  onMonthChange: (month: Date) => void;
}

const weeksOf = (month: Date): Date[][] => {
  const first = startOfWeek(startOfMonth(month), WEEK_OPTIONS);
  return Array.from({ length: 6 }, (_, week) =>
    Array.from({ length: 7 }, (_, day) => addDays(first, week * 7 + day)),
  );
};

const STEP: Record<string, number> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -7,
  ArrowDown: 7,
};

/**
 * Un mes, en español, sin `<input type="date">`.
 *
 * The native control prints the operating system's locale — «07/14/2026» on a
 * machine set to English — inside a product that writes «14 jul 2026»
 * everywhere else, and gives us no say over it. This grid is the same date
 * vocabulary as the rest of the app, and it is reachable by keyboard: one tab
 * stop, arrows to move, Enter to choose.
 */
export function MonthGrid({ month, selected, onSelect, onMonthChange }: MonthGridProps) {
  const container = useRef<HTMLDivElement>(null);
  const today = startOfDay(new Date());
  const [focus, setFocus] = useState<Date>(() => selected ?? today);

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const days = STEP[event.key];
    const next =
      days !== undefined
        ? addDays(focus, days)
        : event.key === 'Home'
          ? startOfWeek(focus, WEEK_OPTIONS)
          : event.key === 'End'
            ? endOfWeek(focus, WEEK_OPTIONS)
            : event.key === 'PageUp'
              ? addMonths(focus, -1)
              : event.key === 'PageDown'
                ? addMonths(focus, 1)
                : null;
    if (!next) return;

    event.preventDefault();
    setFocus(next);
    if (!isSameMonth(next, month)) onMonthChange(startOfMonth(next));
    // After the month re-renders: the button being focused may not exist yet.
    requestAnimationFrame(() =>
      container.current
        ?.querySelector<HTMLButtonElement>(`[data-day="${format(next, KEY)}"]`)
        ?.focus(),
    );
  };

  return (
    <div ref={container} onKeyDown={move}>
      <div aria-hidden className="grid grid-cols-7 pb-1 text-center text-xs text-muted-foreground">
        {weeksOf(month)[0]?.map((day) => (
          <span key={day.toISOString()}>{format(day, 'EEEEE', { locale: es })}</span>
        ))}
      </div>

      <div role="grid" aria-label="Días del mes" className="grid grid-cols-7 gap-0.5">
        {weeksOf(month)
          .flat()
          .map((day) => {
            const outside = !isSameMonth(day, month);
            const isSelected = selected !== null && isSameDay(day, selected);
            return (
              <button
                key={day.toISOString()}
                type="button"
                role="gridcell"
                data-day={format(day, KEY)}
                tabIndex={isSameDay(day, focus) ? 0 : -1}
                aria-selected={isSelected}
                aria-label={format(day, "d 'de' MMMM 'de' yyyy", { locale: es })}
                onFocus={() => setFocus(day)}
                onClick={() => onSelect(day)}
                className={cn(
                  'grid h-8 place-items-center rounded-md text-sm tabular transition-colors duration-150 ease-out',
                  outside ? 'text-muted-foreground/60' : 'hover:bg-muted',
                  isSameDay(day, today) && !isSelected && 'font-medium text-brand',
                  isSelected && 'bg-primary text-primary-foreground hover:bg-primary',
                )}
              >
                {format(day, 'd')}
              </button>
            );
          })}
      </div>
    </div>
  );
}
